/**
 * The test ids this domain renders, and the nav item ids they are built from.
 *
 * **This is a leaf, and `e2e/` imports it directly rather than through `@/navigation`.**
 * Publishing these from the face and importing the face from a spec takes
 * `npx playwright test --list` from 17 tests to zero: every component in this domain imports
 * `@phosphor-icons/react`, which fails under Playwright's loader with `Unexpected module status 5`.
 * Probed four ways on #365 — the trigger is the package, not React, JSX, `'use client'`, or the
 * barrel shape. So this module imports nothing, and the face re-exports it for `src/`.
 *
 * ADR 0014:118-123 is why the strings live here at all: a spec that repeats `'sidebar'` goes green
 * when the render site is deleted, where a spec that imports the constant goes red.
 *
 * `right-panel` is the thirteenth id a spec consumes and is deliberately absent — it belonged to
 * `components/layout/right-panel.tsx`, which #317 deleted with #157.
 */

/**
 * Every `id` in `NAV_CONFIG`, and the source of truth for it: `NavItemConfig.id` is typed
 * `NavItemId`, so adding a nav item without adding it here is a compile error rather than a test id
 * nothing can name.
 */
export const NAV_ITEM_IDS = [
  'today',
  'upcoming',
  'projects',
  'journal',
  'people',
  'movies',
  'books',
  'music',
] as const

export type NavItemId = (typeof NAV_ITEM_IDS)[number]

/** The test id `sidebar-nav.tsx` renders on each nav link. */
export function navItemTestId<T extends NavItemId>(id: T): `nav-${T}` {
  return `nav-${id}`
}

export const SIDEBAR_TEST_ID = 'sidebar'
export const MOBILE_TOP_BAR_TEST_ID = 'mobile-top-bar'
export const MOBILE_DRAWER_TEST_ID = 'mobile-drawer'
export const MOBILE_DRAWER_BACKDROP_TEST_ID = 'mobile-drawer-backdrop'
